import { Link, useParams } from 'react-router'
import { Lock, Moon, Target } from 'lucide-react'
import { UserAvatar } from '@/components/composites/user-avatar'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { formatClock, formatLogged } from '@/features/worktime/clock'
import type { TeamPerson } from './api'
import { formatTargetMinutes, liveTodayMs, targetProgress } from './presence'

/** What one person is doing, in the same words on the team page and at a desk in the office. */
export function PersonStatus({
  person,
  fetchedAt,
  now,
  compact = false,
}: {
  person: TeamPerson
  fetchedAt: number
  now: number
  compact?: boolean
}) {
  const { workspaceSlug = '' } = useParams()
  const today = liveTodayMs(person, fetchedAt, now)
  const progress = targetProgress(today, person.target?.minutes)
  const sinceStart = person.startedAt ? Math.max(0, now - Date.parse(person.startedAt)) : 0
  return (
    <div className="grid gap-1.5 text-sm">
      <p className="flex items-center gap-1.5">
        {person.working ? (
          <>
            <span className="relative flex size-2 shrink-0" aria-hidden>
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-ios-green opacity-60 motion-reduce:animate-none" />
              <span className="relative inline-flex size-2 rounded-full bg-ios-green" />
            </span>
            {person.card ? (
              <Link to={paths.workItem(workspaceSlug, person.card.id)} className="min-w-0 truncate font-medium hover:underline" title={person.card.title}>
                {person.card.code ? `${person.card.code} · ` : ''}
                {person.card.title}
              </Link>
            ) : (
              <span className="flex items-center gap-1 text-text-secondary">
                <Lock className="size-3" /> A private item
              </span>
            )}
          </>
        ) : (
          <span className="flex items-center gap-1.5 text-text-muted">
            <Moon className="size-3.5" /> Not timing anything
          </span>
        )}
      </p>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-muted">
        {person.working ? (
          <span className="num font-semibold tabular-nums text-foreground" role="timer" aria-label="Time on the current item">
            {formatClock(sinceStart)}
          </span>
        ) : null}
        <span>
          <span className="num tabular-nums">{formatLogged(today)}</span> today
        </span>
      </p>
      {person.target ? (
        <div className={cn('rounded-md bg-surface-sunken px-2 py-1.5', compact && 'text-xs')}>
          <p className="flex items-start gap-1.5 text-text-secondary">
            <Target className="mt-0.5 size-3.5 shrink-0 text-brand" />
            <span className="min-w-0">
              {person.target.text ?? 'Time target'}
              {person.target.minutes ? <span className="text-text-muted"> · {formatTargetMinutes(person.target.minutes)}</span> : null}
            </span>
          </p>
          {progress !== null ? (
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-border" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
              <div className={cn('h-full rounded-full transition-[width] dur-base', progress >= 1 ? 'bg-success' : 'bg-brand')} style={{ width: `${progress * 100}%` }} />
            </div>
          ) : null}
        </div>
      ) : null}
      {person.priorities && person.priorities.length > 0 ? (
        <div className={cn('rounded-md bg-surface-sunken px-2 py-1.5', compact && 'text-xs')}>
          <p className="mb-0.5 text-tiny font-medium text-text-muted">Priorities today</p>
          <ul className="space-y-0.5">
            {person.priorities.slice(0, compact ? 3 : 10).map((line) => (
              <li key={line.id} className={cn('flex items-start gap-1.5 text-text-secondary', line.done && 'line-through opacity-60')}>
                <span aria-hidden className="mt-1.5 size-1 shrink-0 rounded-full bg-brand" />
                <span className="min-w-0">{line.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

export function PersonHeader({ person }: { person: TeamPerson }) {
  return (
    <div className="flex items-center gap-2">
      <UserAvatar person={{ id: person.userId, displayName: person.displayName, photoUrl: person.photoUrl }} size="md" />
      <p className="min-w-0 truncate text-sm font-semibold">{person.displayName}</p>
    </div>
  )
}
